import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Layouts
import MainLayout from "layouts/Main";

// Components
import {
  Button,
  InputField,
  Link,
  SupportingMessage,
} from "@octopus-investments/deep-sea-components";
import PasswordField from "components/Inputs/PasswordField/PasswordField";

// Actions
import { checkEmail } from "store/slices/accountSlice";
import {
  setBackButtonTarget,
  setContent,
  setEmailAddress,
  setTwoFactorEnabled,
} from "store/slices/applicationSlice";
import { setLoginOrRegisterButtonLoader } from "store/slices/loadersSlice";

// Tools
import useWindowSize from "hooks/useWindowSize";
import { VALIDATION_ERRORS } from "utils/constants";
import { focusLastInputFieldCharacter, validateEmail } from "utils/helpers";

// Tracking
import { ampli } from "ampli";

// Styles
import "./LoginOrRegisterPage.scss";

export default function LoginOrRegisterPage() {
  const { isLoginOrRegisterButtonLoader } = useSelector(({ loaders }) => loaders);
  const { email: emailAddress, isTwoFactorEnabled } = useSelector(({ application }) => application);

  const [email, setEmail] = useState(emailAddress);
  const [showError, setShowError] = useState(false);

  const { isMobile } = useWindowSize();

  // Redux
  const reduxDispatch = useDispatch();

  function handleEmailChange(e) {
    setEmail(e.value);
    if (showError) setShowError(false);
  }

  function handleNextButton() {
    const isEmailValid = Boolean(validateEmail(email));
    setShowError(!isEmailValid);

    focusLastInputFieldCharacter("enterYourEmailInput");

    if (isEmailValid) {
      onSubmit();
    } else {
      ampli.loginRegistrationIntended({
        di_email_criteria_met: false,
        di_email_entered: email,
        is_user_registered: false,
        mfa_status: isTwoFactorEnabled,
      });
    }
  }

  function onSubmit() {
    reduxDispatch(setLoginOrRegisterButtonLoader(true));

    reduxDispatch(setEmailAddress(email));

    reduxDispatch(checkEmail()).then(result => {
      const { c, success, e, r = false, t = false } = result.payload;

      ampli.loginRegistrationIntended({
        di_email_criteria_met: true,
        di_email_entered: email,
        is_user_registered: r,
        mfa_status: t,
      });

      if (success === false) return reduxDispatch(setLoginOrRegisterButtonLoader(false));

      let content = "";

      if (r) content = "login";
      else if (e === false) content = "confirmAccount";
      else if (e === true && c === true) content = "tokenExpired";
      else content = "checkMail";

      reduxDispatch(setTwoFactorEnabled(t));
      reduxDispatch(setBackButtonTarget("loginOrRegister"));
      reduxDispatch(setContent(content));
    });
  }

  function renderAdminAccountRequestLink() {
    function onAdminRequestClicked() {
      ampli.adminRegistrationIntended();
      return true;
    }

    return (
      <>
        Need an administrator account? Request one{" "}
        <Link
          className="fw-bold"
          href={process.env.REACT_APP_ADMIN_LOGN_URL}
          onPointerDown={onAdminRequestClicked}
          target="_blank"
        >
          here
        </Link>
        .
      </>
    );
  }

  return (
    <MainLayout>
      <div className="login-or-register-content">
        <div className="login-or-register-form">
          <h1 className="coheadline navyBlue">Log in or register to manage your account</h1>

          <div className="enter-your-email-block ta-l">
            <InputField
              autoComplete="on"
              autocompletetype="email"
              autoFocus
              value={email}
              error={showError}
              id="enterYourEmailInput"
              label="Email address"
              name="email"
              onChange={handleEmailChange}
              onKeyPress={e => {
                if (e.which === 13 && !isLoginOrRegisterButtonLoader) handleNextButton();
              }}
              placeholder="Enter your email address"
              required
              showError={showError}
              type="email"
            />
            {showError && (
              <SupportingMessage
                status="error"
                message={VALIDATION_ERRORS[email === "" ? "emptyField" : "wrongPattern"]}
              />
            )}

            {/* We hide this field from customer but it needed for autofill functionality. */}
            <PasswordField />
            {!isMobile && (
              <div className="admin-account-request-link-content">
                {renderAdminAccountRequestLink()}
              </div>
            )}
            <Button
              customWidth="140px"
              disabled={isLoginOrRegisterButtonLoader}
              isLoading={isLoginOrRegisterButtonLoader}
              onClick={handleNextButton}
              label="Next"
              variant="solid"
            />
          </div>
        </div>
        {isMobile && (
          <div className="admin-account-request-link-content">
            {renderAdminAccountRequestLink()}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
